// src/app/_components/space-admin/space-admin.component.ts
// Space admin page (tasks/space_core_client.md Stage 3, widened per an explicit request —
// see space.service.ts's module comment): lets a Space's OWNER/ADMIN rename it, set its
// description, manage the Space's own tags (shared <app-tag-manager>), and grant/change/revoke
// any active NotesApp user's role in it. Reached via the
// Edit button on a SHARED Space in <app-space-picker> (Home → user menu → Spaces).
//
// Routed full page (not a dialog) — matches this app's existing "admin-style screen"
// precedent (ManageComponent/ManageAttachmentsComponent), not the Spaces picker's own dialog
// chrome, since navigating here closes that dialog by tearing down HomeComponent's route.

import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

import { ActiveUser, Space, SpaceMember, SpaceRole, SpaceService } from '../../_service/space.service';
import { TagManagerComponent } from '../tag-manager/tag-manager.component';
import { SnackbarService } from '../../_service/snackbar.service';

interface MemberRow {
  userId: string;
  username: string;
  role: SpaceRole | null;
  saving: boolean;
}

const ROLE_RANK: Record<SpaceRole, number> = { OWNER: 0, ADMIN: 1, EDITOR: 2, VIEWER: 3 };

export type SpaceAdminSection = 'details' | 'tags' | 'members';
/** Which sections the user collapsed (this device, every Space). */
export const SPACE_ADMIN_COLLAPSED_KEY = 'notesapp.spaceAdmin.collapsedSections';

@Component({
  selector: 'app-space-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    TagManagerComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './space-admin.component.html',
})
export class SpaceAdminComponent implements OnInit {
  spaceId = '';
  space: Space | null = null;

  loading = true;
  loadError: string | null = null;
  notAuthorized = false;

  editName = '';
  editDescription = '';
  savingSpace = false;
  saveSpaceError: string | null = null;

  members: MemberRow[] = [];

  private collapsedSections = new Set<SpaceAdminSection>(SpaceAdminComponent.readCollapsed());

  isCollapsed(section: SpaceAdminSection): boolean {
    return this.collapsedSections.has(section);
  }

  toggleSection(section: SpaceAdminSection): void {
    if (this.collapsedSections.has(section)) this.collapsedSections.delete(section);
    else this.collapsedSections.add(section);
    try {
      localStorage.setItem(SPACE_ADMIN_COLLAPSED_KEY, JSON.stringify([...this.collapsedSections]));
    } catch {
      // Storage unavailable: the state just isn't remembered.
    }
  }

  private static readCollapsed(): SpaceAdminSection[] {
    try {
      const saved = JSON.parse(localStorage.getItem(SPACE_ADMIN_COLLAPSED_KEY) ?? '[]');
      return Array.isArray(saved) ? saved.filter((s): s is SpaceAdminSection => s === 'details' || s === 'tags' || s === 'members') : [];
    } catch {
      return [];
    }
  }

  readonly roleOptions: { label: string; value: SpaceRole | null }[] = [
    { label: 'No access', value: null },
    { label: 'Viewer', value: 'VIEWER' },
    { label: 'Editor', value: 'EDITOR' },
    { label: 'Admin', value: 'ADMIN' },
    { label: 'Owner', value: 'OWNER' },
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly spaceService: SpaceService,
    private readonly snackbar: SnackbarService,
    private readonly confirmationService: ConfirmationService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.spaceId = this.route.snapshot.paramMap.get('spaceId') ?? '';
    this.load();
  }

  back(): void {
    this.router.navigate(['/home']);
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.notAuthorized = false;

    forkJoin({
      spaces: this.spaceService.getSpaces(),
      members: this.spaceService.getMembers(this.spaceId),
      users: this.spaceService.getActiveUsers(),
    }).subscribe({
      next: ({ spaces, members, users }) => this.applyLoaded(spaces, members, users),
      error: (err) => {
        this.loadError = err?.message || 'Could not load this Space.';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private applyLoaded(spaces: Space[], members: SpaceMember[], users: ActiveUser[]): void {
    const space = spaces.find((s) => s.id === this.spaceId) ?? null;
    this.space = space;
    this.loading = false;

    if (!space) {
      this.loadError = 'Space not found.';
      this.cdr.markForCheck();
      return;
    }

    // Server-side, MANAGE_SPACE/MANAGE_MEMBERS are re-checked on every write below regardless
    // — this is UX only, so a direct link/refresh with a stale role can't be used to bypass it.
    if (space.current_user_role !== 'OWNER' && space.current_user_role !== 'ADMIN') {
      this.notAuthorized = true;
      this.cdr.markForCheck();
      return;
    }

    this.editName = space.name;
    this.editDescription = space.description ?? '';

    const roleByUserId = new Map(members.map((m) => [m.user_id, m.role]));
    this.members = users
      .map((u) => ({ userId: u.id, username: u.username, role: roleByUserId.get(u.id) ?? null, saving: false }))
      .sort((a, b) => {
        const rankA = a.role ? ROLE_RANK[a.role] : ROLE_RANK.VIEWER + 1;
        const rankB = b.role ? ROLE_RANK[b.role] : ROLE_RANK.VIEWER + 1;
        if (rankA !== rankB) return rankA - rankB;
        return a.username.localeCompare(b.username);
      });

    this.cdr.markForCheck();
  }

  get spaceInfoDirty(): boolean {
    if (!this.space) return false;
    return this.editName.trim() !== this.space.name || this.editDescription.trim() !== (this.space.description ?? '');
  }

  saveSpaceInfo(): void {
    if (!this.space || this.savingSpace) return;
    const name = this.editName.trim();
    if (!name) {
      this.saveSpaceError = 'Name is required.';
      return;
    }

    const changes: { name?: string; description?: string } = {};
    if (name !== this.space.name) changes.name = name;
    const description = this.editDescription.trim();
    if (description !== (this.space.description ?? '')) changes.description = description;
    if (Object.keys(changes).length === 0) return;

    this.savingSpace = true;
    this.saveSpaceError = null;
    this.spaceService.updateSpace(this.space.id, changes).subscribe({
      next: (updated) => {
        this.space = updated;
        this.editName = updated.name;
        this.editDescription = updated.description ?? '';
        this.savingSpace = false;
        this.snackbar.showSuccessMessage('Space updated.');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.saveSpaceError = err?.message || 'Could not update the Space.';
        this.savingSpace = false;
        this.cdr.markForCheck();
      },
    });
  }

  onRoleSelected(row: MemberRow, newRole: SpaceRole | null): void {
    if (newRole === row.role || row.saving) return;

    if (newRole === null) {
      this.confirmationService.confirm({
        message: `Remove ${row.username}'s access to "${this.space?.name}"? They will no longer be able to see or edit its notes.`,
        header: 'Remove access',
        icon: 'pi pi-exclamation-triangle',
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.removeMemberRow(row),
      });
      return;
    }

    if (row.role === null) {
      this.addMemberRow(row, newRole);
    } else {
      this.updateMemberRoleRow(row, newRole);
    }
  }

  private addMemberRow(row: MemberRow, role: SpaceRole): void {
    row.saving = true;
    this.spaceService.addMember(this.spaceId, row.userId, role).subscribe({
      next: () => {
        row.role = role;
        row.saving = false;
        this.snackbar.showSuccessMessage(`${row.username} added as ${role.toLowerCase()}.`);
        this.cdr.markForCheck();
      },
      error: (err) => this.onRoleChangeError(row, err),
    });
  }

  private updateMemberRoleRow(row: MemberRow, role: SpaceRole): void {
    row.saving = true;
    this.spaceService.updateMemberRole(this.spaceId, row.userId, role).subscribe({
      next: () => {
        row.role = role;
        row.saving = false;
        this.snackbar.showSuccessMessage(`${row.username}'s role changed to ${role.toLowerCase()}.`);
        this.cdr.markForCheck();
      },
      error: (err) => this.onRoleChangeError(row, err),
    });
  }

  private removeMemberRow(row: MemberRow): void {
    row.saving = true;
    this.spaceService.removeMember(this.spaceId, row.userId).subscribe({
      next: () => {
        row.role = null;
        row.saving = false;
        this.snackbar.showSuccessMessage(`${row.username}'s access removed.`);
        this.cdr.markForCheck();
      },
      error: (err) => this.onRoleChangeError(row, err),
    });
  }

  private onRoleChangeError(row: MemberRow, err: any): void {
    row.saving = false;
    this.snackbar.showErrorMessage(err?.message || 'Could not update that member.');
    this.cdr.markForCheck();
  }

  trackByUserId(_index: number, row: MemberRow): string {
    return row.userId;
  }
}
